import {assert} from 'Asserts';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from '../../processFlowchartConfigEditorConstants';
import {
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {
  buildTransitionIndexByPTDVPair,
  getFieldPairKey,
  getOrCreateTransition,
  getPTDVPairKey,
} from './transitionKeyUtils';
import {findTaskSchemaNodePair} from './transitionLookupUtils';
import {Transition} from './TransitionTypes';

export interface CreateTransitionResult {
  transition: Transition;
  /** Whether a new transition was created */
  isNew: boolean;
}

export interface ShouldDeleteTransitionResult {
  shouldDelete: boolean;
  transitionId: string | null;
}

function getPTDVIdsFromEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>
): {sourcePTDVId: string; targetPTDVId: string} | null {
  const nodePair = findTaskSchemaNodePair(edge, nodes);
  if (!nodePair) {
    return null;
  }

  return {
    sourcePTDVId: nodePair.sourceNode.taskDefinitionVersionSpec.clientId,
    targetPTDVId: nodePair.targetNode.taskDefinitionVersionSpec.clientId,
  };
}

function getFieldPairKeyFromDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>
): string | null {
  if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
  if (!ptdvIds) {
    return null;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  return getFieldPairKey({
    sourcePTDVId: ptdvIds.sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId: ptdvIds.targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });
}

// TODO(BNCH-139872): This should handle merging transitions
export function shouldDeleteTransition(
  removedEdge: FlowchartStaticEdgeData,
  allEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): ShouldDeleteTransitionResult {
  if (removedEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return {shouldDelete: false, transitionId: null};
  }

  const ptdvIds = getPTDVIdsFromEdge(removedEdge, nodes);
  if (!ptdvIds) {
    return {shouldDelete: false, transitionId: null};
  }

  const {sourcePTDVId, targetPTDVId} = ptdvIds;
  const index = buildTransitionIndexByPTDVPair(transitions);
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const transitionId = index[pairKey]?.[0];

  if (!transitionId) {
    return {shouldDelete: false, transitionId: null};
  }

  // Build nodeId -> PTDVId map for task schema nodes
  const nodeToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }

  // Check if any other control flow edges exist between the same PTDV pair
  const hasOtherEdges =
    allEdges.some((edge) => {
      if (edge.id === removedEdge.id || edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
        return false;
      }

      const edgeSourcePTDVId = nodeToPTDVId.get(edge.source);
      const edgeTargetPTDVId = nodeToPTDVId.get(edge.target);

      return edgeSourcePTDVId === sourcePTDVId && edgeTargetPTDVId === targetPTDVId;
    }) || index[pairKey]?.length > 1;

  return {
    shouldDelete: !hasOtherEdges,
    transitionId: hasOtherEdges ? null : transitionId,
  };
}

/**
 * Find all transitions affected by changes to nodes or edges.
 * Useful for determining which transitions need updates when the flowchart changes.
 */
export function getAffectedTransitions(
  changedNodeIds: Array<string>,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): Array<string> {
  if (changedNodeIds.length === 0) {
    return [];
  }

  const changedPTDVIds = new Set<string>();
  for (const nodeId of changedNodeIds) {
    const node = nodes.find((n) => n.id === nodeId);
    if (node && isStaticTaskSchemaNodeData(node)) {
      changedPTDVIds.add(node.taskDefinitionVersionSpec.clientId);
    }
  }

  if (changedPTDVIds.size === 0) {
    return [];
  }

  const affectedTransitionIds: Array<string> = [];
  for (const transition of Object.values(transitions)) {
    const involvesPTDV =
      transition.sourcePTDVIds.some((id) => changedPTDVIds.has(id)) ||
      changedPTDVIds.has(transition.targetPTDVId);

    if (involvesPTDV) {
      affectedTransitionIds.push(transition.id);
    }
  }

  return affectedTransitionIds;
}

export function addCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  customMappingData: CustomMappingData = {}
): Record<string, CustomMappingData> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: customMappingData,
  };
}

export function removeCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition
): Record<string, CustomMappingData> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  const {[fieldPairKey]: _, ...remainingMappings} = transition.customMappingDataByFieldPair;
  return remainingMappings;
}

/**
 * Creates transitions for a newly added control flow edge.
 * Handles three cases:
 * 1. Direct task-to-task edge: Creates a single transition
 * 2. Router-to-task edge: Creates transitions from all upstream task nodes to the target
 * 3. Task-to-router edge: Creates transitions from the source to all downstream task nodes
 *
 * @returns Array of new transitions to be added (excludes transitions that already exist)
 */
export function createTransitionsForNewControlFlowEdge(
  newEdge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  existingTransitions: Record<string, Transition>
): Array<Transition> {
  if (newEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return [];
  }

  const sourceNode = nodes.find((n) => n.id === newEdge.source);
  const targetNode = nodes.find((n) => n.id === newEdge.target);

  if (sourceNode == null || targetNode == null) {
    return [];
  }

  const newTransitions: Array<Transition> = [];
  const workingTransitions = {...existingTransitions};

  const addTransitionIfNew = (sourcePTDVId: string, targetPTDVId: string): void => {
    const {transition, isNew} = getOrCreateTransition({
      sourcePTDVId,
      targetPTDVId,
      transitions: workingTransitions,
      isDefault: true,
    });
    if (isNew) {
      newTransitions.push(transition);
    }
  };

  // Case 1: Direct task-to-task edge
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    addTransitionIfNew(
      sourceNode.taskDefinitionVersionSpec.clientId,
      targetNode.taskDefinitionVersionSpec.clientId
    );
  }

  // Case 2: Router-to-task edge - create transitions from all upstream task nodes
  if (isStaticRouterNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const incomingEdges = allEdges.filter(
      (e) => e.target === sourceNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    assert(incomingEdges.length <= 1, 'Expected at most one incoming edge');
    const upstreamNode = nodes.find((n) => n.id === incomingEdges[0]?.source);
    if (upstreamNode != null && isStaticTaskSchemaNodeData(upstreamNode)) {
      addTransitionIfNew(upstreamNode.taskDefinitionVersionSpec.clientId, targetPTDVId);
    }
  }

  // Case 3: Task-to-router edge - create transitions to all downstream task nodes
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticRouterNodeData(targetNode)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const outgoingEdges = allEdges.filter(
      (e) => e.source === targetNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    for (const outgoingEdge of outgoingEdges) {
      const downstreamNode = nodes.find((n) => n.id === outgoingEdge.target);
      if (downstreamNode != null && isStaticTaskSchemaNodeData(downstreamNode)) {
        addTransitionIfNew(sourcePTDVId, downstreamNode.taskDefinitionVersionSpec.clientId);
      }
    }
  }

  return newTransitions;
}
